import { LocalStackEventType, eventSystem, STORAGE_KEY_THEME, ThemeType } from '@localstack/integrations';
import { useApiGetter, useRoutes, MembershipService } from '@localstack/services';
import { ContentModal, Dropdown, DropdownProps, LogoRocket, LoadingFragment } from '@localstack/ui';
import { KeyboardArrowDown, DarkMode, Laptop, LightMode } from '@mui/icons-material';
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  MenuItem,
  Stack,
  Theme,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { ReactElement, ReactNode, useState } from 'react';

import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';

import { TEST_IDS } from '@localstack/constants';

import { AppRoute } from '~/config';
import { useSignout } from '~/hooks/useSignout';

import { getSectionItemBackground, getSectionItemTextColor } from './shared';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      display: 'flex',
      maxWidth: 'calc(100% - 26px)', // leaving room for the close button that appears in small screens
    },
    skeleton: {
      minWidth: theme.spacing(15),
    },
    dropDown: {
      minWidth: theme.spacing(30),
      marginLeft: theme.spacing(5),
    },
    dropDownArrow: {
      color: getSectionItemTextColor(theme),
    },
    fakeMenuItem: {
      border: '1px solid transparent',
      borderRadius: 8,
      '&:hover': {
        backgroundColor: theme.palette.background.paper,
      },
    },
    dropdownMenuItem: {
      paddingLeft: theme.spacing(1),
      paddingRight: theme.spacing(1),
    },
    themeToggler: {
      display: 'flex',
      justifyContent: 'space-between',
      gap: '1rem',
      width: '100%',
      '&:hover': {
        backgroundColor: 'unset !important',
        cursor: 'unset !important',
        color: 'unset !important',
        '& svg': {
          color: 'unset !important',
        },
      },
    },
    menuItem: {
      marginTop: '1px',
      padding: '0.3rem 0.2rem',
      '&:hover': {
        background: getSectionItemBackground(theme),
      },
      '&.active': {
        background: getSectionItemBackground(theme),
      },
      borderRadius: 8,
    },
    label: {
      marginLeft: '0.5em',
      color: getSectionItemTextColor(theme),
    },
  }),
);

const TextWithLoading = ({ loading, children }: { children: ReactNode; loading: boolean }): ReactElement => (
  <Box component="span" display="flex" alignItems="center" gap={1} justifyContent="center">
    <span>{children}</span>
    <CircularProgress size={12} sx={{ opacity: loading ? 1 : 0 }} />
  </Box>
);

export const WorkspaceSelector = (): ReactElement => {
  const classes = useStyles();
  const { goto } = useRoutes();
  const currentTheme = localStorage.getItem(STORAGE_KEY_THEME);
  const { handleSignOut, isSigningOut, isGlobalSigningOut } = useSignout();
  const [isSignOutModalOpen, setIsSignOutModalOpen] = useState(false);

  const handleChangeTheme = (theme: ThemeType) => {
    eventSystem.notify({ eventType: LocalStackEventType.THEME_UPDATE, data: { theme } });
  };

  const { data: primaryMembership, isLoading: isMembershipLoading } = useApiGetter(
    MembershipService,
    'getPrimaryMembership',
    [],
  );

  return (
    <div data-name="workspace selector" className={classes.root}>
      <Dropdown
        size="small"
        dropdownClassName={classes.dropDown}
        menuListProps={{
          disablePadding: true,
        }}
        renderButton={({ onClick }: DropdownProps) => (
          <MenuItem
            data-testid={TEST_IDS.WORKSPACE_SELECTOR}
            className={classes.menuItem}
            sx={{ maxWidth: '100%' }}
            onClick={onClick}
            tabIndex={0}
          >
            <Stack
              direction="row"
              sx={{
                width: '100%',
                alignItems: 'center',
              }}
            >
              <LogoRocket size="small" />
              <LoadingFragment
                loading={isMembershipLoading}
                skeletonClassName={`${classes.label} ${classes.skeleton}`}
                size={1}
              >
                <Typography
                  className={classes.label}
                  style={{
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textAlign: 'left',
                    fontWeight: 500,
                  }}
                >
                  {primaryMembership?.org_name}
                </Typography>
              </LoadingFragment>
              <KeyboardArrowDown className={classes.dropDownArrow} fontSize="small" opacity={0.5} />
            </Stack>
          </MenuItem>
        )}
      >
        {(onClose) => (
          <>
            <MenuItem
              className={`${classes.menuItem} ${classes.dropdownMenuItem}`}
              onClick={() => goto(AppRoute.SETTINGS_PROFILE)}
              tabIndex={0}
            >
              Settings
            </MenuItem>
            <MenuItem
              className={`${classes.menuItem} ${classes.themeToggler} ${classes.dropdownMenuItem}`}
              disableRipple
            >
              Theme
              <ToggleButtonGroup
                exclusive
                onChange={(_e, newValue) => (newValue ? handleChangeTheme(newValue as ThemeType) : undefined)}
                value={currentTheme}
              >
                <ToggleButton value={ThemeType.SYSTEM} size="xsmall">
                  <Laptop fontSize="inherit" />
                </ToggleButton>
                <ToggleButton value={ThemeType.LIGHT} size="xsmall">
                  <LightMode fontSize="inherit" />
                </ToggleButton>
                <ToggleButton value={ThemeType.DARK} size="xsmall" data-testId={TEST_IDS.DARK_MODE_BUTTON}>
                  <DarkMode fontSize="inherit" />
                </ToggleButton>
              </ToggleButtonGroup>
            </MenuItem>
            <Divider />
            <MenuItem
              className={`${classes.menuItem} ${classes.dropdownMenuItem}`}
              onClick={() => {
                onClose();
                setIsSignOutModalOpen(true);
              }}
              tabIndex={0}
            >
              Sign Out
            </MenuItem>
          </>
        )}
      </Dropdown>
      <ContentModal
        open={isSignOutModalOpen}
        onClose={() => setIsSignOutModalOpen(false)}
        dialogTitle="Signing out"
        modalMaxWidth="xs"
        hideDialogActions
        backdropBlurIntensity={4}
        sx={{
          '& .MuiDialog-paper': { maxWidth: 380, borderRadius: 6 },
        }}
        dialogContent={
          <>
            <Typography variant="body1" color="textSecondary" mb={2} fontSize="1rem">
              Sign out of this device, or all devices at once.
            </Typography>
            <Stack spacing={1} alignItems="center">
              <Button
                variant="contained"
                color="primary"
                fullWidth
                onClick={() => handleSignOut()}
                disabled={isSigningOut || isGlobalSigningOut}
                sx={{ textTransform: 'none' }}
              >
                <TextWithLoading loading={isSigningOut}>Sign out</TextWithLoading>
              </Button>
              <Button
                variant="outlined"
                color="error"
                fullWidth
                onClick={() => handleSignOut({ global: true })}
                disabled={isGlobalSigningOut || isSigningOut}
                sx={{ textTransform: 'none' }}
              >
                <TextWithLoading loading={isGlobalSigningOut}>Sign out from all devices</TextWithLoading>
              </Button>
            </Stack>
          </>
        }
      />
    </div>
  );
};
